import { useLocalStorageState } from "ahooks";

export type CanvasGridMode = "lines" | "dots" | "none";

export type CanvasCursorTool = "arrow" | "hand";

export const CANVAS_GRID_MODES: {
  key: CanvasGridMode;
  name: string;
}[] = [
  { key: "lines", name: "Line grid" },
  { key: "dots", name: "Dot grid" },
  { key: "none", name: "No grid" },
];

export const CANVAS_CURSOR_TOOLS: {
  key: CanvasCursorTool;
  name: string;
}[] = [
  { key: "arrow", name: "Pointer" },
  { key: "hand", name: "Hand" },
];

export const CANVAS_CONTROLS_STORAGE_KEYS = {
  gridMode: "canvas.controls.gridMode",
  cursorTool: "canvas.controls.cursorTool",
  alignmentGuides: "canvas.controls.alignmentGuides",
} as const;

const GRID_MODE_KEYS = new Set<CanvasGridMode>(
  CANVAS_GRID_MODES.map((mode) => mode.key),
);

export function parseCanvasGridMode(
  stored: CanvasGridMode | undefined,
): CanvasGridMode {
  if (stored && GRID_MODE_KEYS.has(stored)) return stored;
  return "lines";
}

/**
 * The canvas grid preference, readable from anywhere.
 *
 * `CanvasUiProvider` only mounts on canvas pages, but the preference itself is
 * just a stored value — so surfaces outside the canvas (the Preferences pane,
 * the account menu) bind it through this hook rather than reaching for canvas
 * context that isn't there. Same storage key, same serializer, so the two stay
 * in step; `listenStorageChange` on both sides makes the update live.
 */
export function useCanvasGridModePreference(): [
  CanvasGridMode,
  (mode: CanvasGridMode) => void,
] {
  const [stored, setStored] = useLocalStorageState<CanvasGridMode>(
    CANVAS_CONTROLS_STORAGE_KEYS.gridMode,
    { defaultValue: "lines", listenStorageChange: true },
  );
  return [parseCanvasGridMode(stored), setStored];
}

export function parseCanvasCursorTool(
  stored: string | undefined,
): CanvasCursorTool {
  if (stored === "arrow" || stored === "hand") return stored;
  // Legacy persisted keys (default → arrow, pointer → hand)
  if (stored === "default") return "arrow";
  if (stored === "pointer") return "hand";
  return "arrow";
}
